/**
 * Nurse role scope (GP-1183 / GP-211).
 *
 * The `nurse` custom role exists in the backend permission catalog
 * (`backend-v2/src/user-management/enums/custom-role.enum.ts`) but its coarse
 * boolean permissions cannot express what the role actually needs: a nurse
 * keeps Delivery Status while losing every other Main Operations queue, even
 * though all of them are gated on the same `viewDeliveries` / `viewPatients`
 * flags. This module is the ONE place that describes the nurse's UI scope —
 * the sidebar, the router guard, and the per-action gates all read from here so
 * a new blocked screen means one edit, not five.
 *
 * Nurse scope, per the recorded spec:
 *   Nav      → Delivery Status (view only, incl. Clinic Shipments),
 *              Communication Center, Find Patient.
 *   Hidden   → Operations Command Center, every other Main Operations queue,
 *              Subscription Management, Kroll Faxes, Task Queue,
 *              Reimbursement Tracker, Testing Center, Clinic Management,
 *              Driver Management.
 *
 * GP-1267 update: the Clinic Management nav group was originally kept for the
 * nurse but held nothing the role uses, so it is now hidden by section name
 * (see NURSE_BLOCKED_SECTIONS). Its routes stay path-reachable — the group is
 * simply removed from the nurse's sidebar to cut clutter, not locked at the
 * router (unlike the operations queues above).
 *   Patient 360 → read-only except Message, Allergies Add/Manage, contact
 *              info / addresses, and note creation. No order creation, no
 *              subscription creation or editing.
 */

/** The database custom-role key. Must match CustomRole.NURSE on the backend. */
export const NURSE_ROLE = 'nurse';

/**
 * Routes a nurse may not reach — hidden in the sidebar AND blocked at the
 * router, so typing the URL directly is equally closed. Matched on exact path
 * or path-prefix (`/tasks` also covers `/tasks/123`), case-insensitively
 * (the PHOX Entry route is registered as `/order-fulfillment/Phox-entry`).
 */
export const NURSE_BLOCKED_PATHS: readonly string[] = [
  // Operations Command Center — "a nurse doesn't really need the command
  // center", and its KPI tiles only link into queues the nurse can't open.
  '/order-fulfillment/operations-dashboard',
  // Main Operations — everything except Delivery Status.
  '/order-fulfillment/Phox-entry',
  '/order-fulfillment/phox-entry-queue', // PRX-143
  '/order-fulfillment/billing-queue',
  '/order-fulfillment/schedule-delivery',
  '/order-fulfillment/clinical-verification',
  '/order-fulfillment/packaging',
  '/order-fulfillment/product-verification',
  '/order-fulfillment/ready-to-ship',
  '/order-fulfillment/staging-area',
  '/order-fulfillment/all-orders',
  // Standalone items.
  '/order-fulfillment/subscription-management',
  '/kroll-faxes',
  '/tasks',
  '/crm/reimbursements',
  '/psp-queue', // PRX-129
  '/testing-center',
  // PRX-139: the Calendar. Blocked by default until Walid and Rafik decide
  // whether nurses book or see appointments (ADR-120).
  '/calendar',
  // Reports (GP-1327): the hub gained a sidebar entry gated on report
  // permissions, and the nurse role holds `viewReports` — without this the
  // nurse's nav would silently widen past the three items the scope allows.
  '/reports',
  // Driver Management group.
  '/driver-management',
  '/kiosk',
  '/bag-history',
  '/driver-checkout-history',
];

/**
 * Sidebar section headers hidden for nurses (their items go with them).
 * CLINIC MANAGEMENT is hidden by name (GP-1267): its header carries the
 * `editClinicalData` permission the nurse role actually holds, so only a
 * name-based block removes it from the nurse's sidebar.
 */
export const NURSE_BLOCKED_SECTIONS: readonly string[] = [
  'CLINIC MANAGEMENT',
  'DRIVER MANAGEMENT',
];

/**
 * Where a blocked nurse navigation lands. Delivery Status is the one
 * operations screen the role keeps, so it doubles as the nurse home page —
 * a nurse whose saved default landing page is a blocked queue (or who lands on
 * the Operations Command Center default) still gets a working screen instead of
 * an /unauthorized dead end.
 */
export const NURSE_LANDING_PATH = '/order-fulfillment/delivery-status';

/** True when `pathname` is inside the nurse's blocked set. */
export function isNurseBlockedPath(pathname: string): boolean {
  const path = (pathname || '/').toLowerCase().replace(/\/+$/, '') || '/';
  return NURSE_BLOCKED_PATHS.some((blocked) => {
    const b = blocked.toLowerCase();
    return path === b || path.startsWith(`${b}/`);
  });
}
